import { useEffect, useState } from 'react';
import { api } from '../lib/api.ts';
import { formatDay, usePrefs } from '../lib/prefs.tsx';

/**
 * Every company on the instance, and everybody in them.
 *
 * People are folded under the company they belong to, because "which company
 * is this account in" is the question this page exists to answer and a flat
 * list makes the reader carry it in their head.
 *
 * The only screen in the app that reads across companies. Everything else is
 * scoped to one, deliberately and everywhere, so this one is kept separate
 * rather than folded into People with a wider query behind it — a page that
 * sometimes crosses the boundary is a page somebody will widen by accident.
 *
 * Reachable only by a platform administrator. The tab is hidden otherwise, and
 * the server refuses /api/platform/ regardless, which is the half that counts.
 */

type Company = {
  id: number; name: string; slug: string; created_at: string;
  users: number; vehicles: number; scans: number; last_activity: string | null;
  // Null when the company can be deleted, otherwise why it cannot be.
  lockedReason: string | null;
};

type PlatformUser = {
  id: number; email: string; name: string | null; username: string | null;
  role: string; company_id: number; company: string;
  disabled_at: string | null; last_login_at: string | null;
  is_platform_admin: number; sessions: number;
};

export function PlatformPanel({
  you, onView,
}: {
  you: number | null;
  onView: (company: { id: number; name: string }) => void;
}) {
  const { dateFormat } = usePrefs();
  const [data, setData] = useState<{ companies: Company[]; users: PlatformUser[]; platformAdmins: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState('');
  // Whichever deletion is asking to be confirmed, and what has been typed
  // back. One key for both kinds, so opening either closes the other and
  // nothing stays armed behind a collapsed section.
  const [confirming, setConfirming] = useState<{ kind: 'user' | 'company' | 'rename'; id: number } | null>(null);
  const [typed, setTyped] = useState('');
  // `initial` fills the field for a rename, which starts from the current name
  // rather than from nothing.
  const openConfirm = (kind: 'user' | 'company' | 'rename', id: number, initial = '') => {
    const same = confirming?.kind === kind && confirming.id === id;
    setConfirming(same ? null : { kind, id });
    setTyped(same ? '' : initial);
  };
  const closeConfirm = () => {
    setConfirming(null);
    setTyped('');
  };

  const load = () => api.platformOverview().then(setData).catch((e) => setError((e as Error).message));
  useEffect(() => {
    load();
  }, []);

  // `after` is optional because some callers write their own message from the
  // response - what happened is only known once it returns, and a fixed string
  // would throw that away.
  const act = async (fn: () => Promise<unknown>, after?: string) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await fn();
      if (after) setNotice(after);
      await load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  if (error && !data) return <div className="card"><p className="dim small">{error}</p></div>;
  if (!data) return <div className="card"><p className="dim small">Loading the platform…</p></div>;

  // Searching matches people and companies separately, then keeps a company if
  // either side hit. Typing a company name has to find it even when nobody in
  // it matches, and typing an address has to find the company holding it.
  const needle = filter.trim().toLowerCase();
  const matches = (...fields: (string | null | undefined)[]) =>
    fields.some((v) => v?.toLowerCase().includes(needle));

  const users = needle
    ? data.users.filter((u) => matches(u.email, u.name, u.username, u.company, u.role))
    : data.users;
  const companies = needle
    ? data.companies.filter((c) => matches(c.name, c.slug) || users.some((u) => u.company_id === c.id))
    : data.companies;
  // A company matched by its own name shows everybody in it; one matched only
  // by a person shows that person.
  const membersOf = (c: Company) =>
    needle && !matches(c.name, c.slug)
      ? users.filter((u) => u.company_id === c.id)
      : data.users.filter((u) => u.company_id === c.id);

  return (
    <div className="card">
      <div className="row wrap" style={{ gap: 8, marginBottom: 4 }}>
        <h2 style={{ margin: 0, flex: 1 }}>Platform</h2>
        <span className="pill">{data.companies.length} companies</span>
        <span className="pill">{data.users.length} accounts</span>
      </div>
      <p className="small dim" style={{ marginTop: 6 }}>
        Every company on this instance, with its people folded underneath it. This is the only screen that looks
        across companies, so treat what is here as somebody else's data.
      </p>

      <div className="row wrap" style={{ gap: 8, margin: '12px 0' }}>
        <input
          type="search"
          className="platform-filter"
          placeholder="Search people and companies"
          aria-label="Search people and companies"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
        {needle && (
          <span className="small dim" style={{ alignSelf: 'center' }}>
            {companies.length} compan{companies.length === 1 ? 'y' : 'ies'}, {users.length} account
            {users.length === 1 ? '' : 's'}
            {companies.length === 0 && ' — nothing matched'}
          </span>
        )}
      </div>

      {error && <div className="banner error" style={{ marginBottom: 12 }}>{error}</div>}
      {notice && <div className="banner" style={{ marginBottom: 12 }}>{notice}</div>}

      <div className="platform-tree">
        {companies.map((c) => {
          const members = membersOf(c);
          return (
            <details key={c.id} className="platform-company" open={Boolean(needle) || c.id === 1}>
              <summary>
                <span className="platform-company-name">
                  {c.name}
                  <span className="person-username">#{c.id}</span>
                </span>
                <span className="small dim platform-counts">
                  {c.users} user{c.users === 1 ? '' : 's'} · {c.vehicles} vehicle{c.vehicles === 1 ? '' : 's'} ·{' '}
                  {c.scans} scan{c.scans === 1 ? '' : 's'} ·{' '}
                  {c.last_activity ? `active ${formatDay(c.last_activity, dateFormat)}` : 'no scans yet'}
                </span>
              </summary>

              <div className="platform-company-actions">
                <button
                  className="tiny ghost"
                  disabled={c.scans === 0}
                  title={c.scans === 0 ? `${c.name} has no scans yet` : `Read ${c.name}'s fleet and scan history`}
                  onClick={() => onView({ id: c.id, name: c.name })}
                >
                  view history
                </button>
                <button
                  className="tiny ghost"
                  disabled={busy}
                  title={`Rename ${c.name}`}
                  onClick={() => openConfirm('rename', c.id, c.name)}
                >
                  rename company
                </button>
                <button
                  className="tiny ghost"
                  disabled={busy || Boolean(c.lockedReason)}
                  title={c.lockedReason ?? `Delete ${c.name} and everything it holds`}
                  onClick={() => openConfirm('company', c.id)}
                >
                  delete company
                </button>
              </div>

              {confirming?.kind === 'rename' && confirming.id === c.id && (
                <form
                  className="person-confirm is-edit"
                  onSubmit={(e) => {
                    e.preventDefault();
                    const name = typed.trim();
                    act(
                      () => api.platformRenameCompany(c.id, name),
                      `${c.name} is now ${name}.`,
                    ).then(closeConfirm);
                  }}
                >
                  <p className="confirm-ask">
                    Rename <strong>{c.name}</strong> in the field below.
                  </p>
                  <div className="confirm-row">
                    <input
                      className="confirm-input"
                      value={typed}
                      onChange={(e) => setTyped(e.target.value)}
                      placeholder={c.name}
                      maxLength={120}
                      autoFocus
                      autoComplete="off"
                      aria-label={`New name for ${c.name}`}
                    />
                    <button
                      className="tiny"
                      type="submit"
                      disabled={busy || !typed.trim() || typed.trim() === c.name}
                    >
                      save name
                    </button>
                    <button className="tiny ghost" type="button" disabled={busy} onClick={closeConfirm}>
                      cancel
                    </button>
                  </div>
                </form>
              )}

              {confirming?.kind === 'company' && confirming.id === c.id && (
                <form
                  className="person-confirm"
                  onSubmit={(e) => {
                    e.preventDefault();
                    act(async () => {
                      const r = await api.platformDeleteCompany(c.id, typed);
                      setNotice(
                        `${r.deleted} was deleted, with ${r.vehicles} vehicle${r.vehicles === 1 ? '' : 's'} and ${r.scans} scan${r.scans === 1 ? '' : 's'}.`,
                      );
                    }).then(closeConfirm);
                  }}
                >
                  <p className="small">
                    Deleting {c.name} destroys {c.vehicles} vehicle{c.vehicles === 1 ? '' : 's'} and{' '}
                    {c.scans} scan{c.scans === 1 ? '' : 's'}, and removes its {c.users} account
                    {c.users === 1 ? '' : 's'}. None of it can be recovered.
                  </p>
                  {/* The name sits directly above the field, for the same reason it
                      does on an account: it is what has to be copied. */}
                  <p className="confirm-ask">
                    Type <strong>{c.name}</strong> below to confirm.
                  </p>
                  <div className="confirm-row">
                    <input
                      className="confirm-input"
                      value={typed}
                      onChange={(e) => setTyped(e.target.value)}
                      placeholder={c.name}
                      autoFocus
                      autoComplete="off"
                      spellCheck={false}
                      aria-label={`Type ${c.name} to confirm deletion`}
                    />
                    {/* Compared exactly, because the server compares it exactly. */}
                    <button className="tiny danger" type="submit" disabled={busy || typed !== c.name}>
                      delete company
                    </button>
                    <button className="tiny ghost" type="button" disabled={busy} onClick={closeConfirm}>
                      cancel
                    </button>
                  </div>
                </form>
              )}

              {members.length === 0 ? (
                <p className="small dim" style={{ margin: '4px 0 0 0' }}>Nobody in this company yet.</p>
              ) : (
                <div className="people-list">
                  {members.map((u) => {
                    const isYou = u.id === you;
                    const lastAdmin = u.is_platform_admin === 1 && data.platformAdmins === 1;
                    return (
                      <div key={u.id} className={`person-row${u.disabled_at ? ' is-disabled' : ''}`}>
                        <div className="person-main">
                          <span className="person-name">
                            {u.name || u.email}
                            {u.username && <span className="person-username">({u.username})</span>}
                            {isYou && <span className="pill tiny-pill">you</span>}
                            {u.is_platform_admin === 1 && <span className="pill tiny-pill brandish">platform</span>}
                            {u.disabled_at && <span className="pill warn tiny-pill">disabled</span>}
                          </span>
                          <span className="small dim mono">{u.email}</span>
                        </div>

                        <div className="person-state">
                          <span className="small dim">
                            {u.last_login_at
                              ? `Last login ${formatDay(u.last_login_at, dateFormat)}`
                              : 'Never signed in'}
                          </span>
                        </div>

                        <div className="person-actions">
                          <select
                            value={u.role}
                            disabled={busy}
                            onChange={(e) =>
                              act(() => api.platformSetRole(u.id, e.target.value), `${u.email} is now a ${e.target.value}.`)
                            }
                          >
                            {['owner', 'technician', 'viewer', 'customer'].map((r) => (
                              <option key={r} value={r}>{r}</option>
                            ))}
                          </select>
                          <button
                            className="tiny ghost"
                            disabled={busy || isYou}
                            onClick={() =>
                              act(
                                () => api.platformSetDisabled(u.id, !u.disabled_at),
                                u.disabled_at ? `${u.email} can sign in again.` : `${u.email} has been disabled.`,
                              )
                            }
                          >
                            {u.disabled_at ? 'enable' : 'disable'}
                          </button>
                          {/* Deleting is last, after the reversible actions.
                              Typed back to confirm: one row in a list of
                              accounts looks much like the next, and this
                              cannot be undone. */}
                          <button
                            className="tiny ghost"
                            disabled={busy || isYou}
                            title={isYou ? 'You cannot delete your own account' : `Delete ${u.email} permanently`}
                            onClick={() => {
                              openConfirm('user', u.id);
                            }}
                          >
                            delete
                          </button>
                          <button
                            className="tiny ghost"
                            disabled={busy || lastAdmin || (isYou && u.is_platform_admin === 1)}
                            title={
                              lastAdmin
                                ? 'The only platform administrator. Appoint another first.'
                                : 'Platform administrators see and change every company'
                            }
                            onClick={() =>
                              act(
                                () => api.platformSetAdmin(u.id, u.is_platform_admin !== 1),
                                u.is_platform_admin === 1
                                  ? `${u.email} no longer administers the platform.`
                                  : `${u.email} now administers the platform.`,
                              )
                            }
                          >
                            {u.is_platform_admin === 1 ? 'revoke platform' : 'make platform admin'}
                          </button>
                        </div>
                        {confirming?.kind === 'user' && confirming.id === u.id && (
                          <form
                            className="person-confirm"
                            onSubmit={(e) => {
                              e.preventDefault();
                              act(async () => {
                                const r = await api.platformDeleteUser(u.id, typed);
                                setNotice(
                                  `${r.email} has been deleted${r.grantsRemoved ? `, along with ${r.grantsRemoved} vehicle grant${r.grantsRemoved === 1 ? '' : 's'}` : ''}.`,
                                );
                              }).then(closeConfirm);
                            }}
                          >
                            <p className="small">
                              Deleting {u.name || u.email} removes the account and every vehicle
                              shared with it. Their scans stay, because that history belongs to the
                              company that recorded it.
                            </p>
                            {/* The address sits directly above the field, so it can be read
                                and typed without the row scrolling out from under it. */}
                            <p className="confirm-ask">
                              Type <strong>{u.email}</strong> below to confirm.
                            </p>
                            <div className="confirm-row">
                              <input
                                className="confirm-input"
                                value={typed}
                                onChange={(e) => setTyped(e.target.value)}
                                placeholder={u.email}
                                autoFocus
                                autoComplete="off"
                                spellCheck={false}
                                aria-label={`Type ${u.email} to confirm deletion`}
                              />
                              <button
                                className="tiny danger"
                                type="submit"
                                disabled={busy || typed.trim().toLowerCase() !== u.email.toLowerCase()}
                              >
                                delete account
                              </button>
                              <button
                                className="tiny ghost"
                                type="button"
                                disabled={busy}
                                onClick={closeConfirm}
                              >
                                cancel
                              </button>
                            </div>
                          </form>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </details>
          );
        })}
      </div>
    </div>
  );
}
